import { memo, type ReactNode, useCallback, useMemo, useState } from 'react';

import { useResponsiveScale } from '../../hooks/useResponsiveRem';
import { Ellipsis } from '../../icons/lucide/Ellipsis';
import { EllipsisVertical } from '../../icons/lucide/EllipsisVertical';
import {
  Pressable,
  type PressableProps,
  type PressEvent,
} from '../../primitives/Pressable';
import { View } from '../../primitives/View';
import { DARK_LAYER1, DARK_LIFTED } from '../../styles/elevation';
import { useTheme } from '../../theme';
import { cn } from '../../utils/cn';
import { tintHex } from '../../utils/color';
import { subTestID } from '../../utils/testId';
import { Button } from '../Button';
import { MarqueeLabel } from '../MarqueeLabel';
import type { CardBadge } from './CardBadge';
import { CardBodyPanel } from './CardBodyPanel';
import { CardChips } from './CardChips';
import { CardImage } from './CardImage';
import { CARD_NATIVE_SHADOW } from './cardShadows';
import { CardTagLabel } from './CardTagLabel';
import { CardTodoBadge } from './CardTodoBadge';
import { cardTitleVariants, COMPACT_TITLE_PIN, compactPanelRows } from './cardVariants';
import { folderDropCueClass, folderDropCueNativeStyle } from './folderDropCue';
import { FolderPreviewShape } from './FolderShape';
import {
  DEFAULT_PREVIEW_EMOJI,
  FOLDER_PAGE_LAYOUTS,
  FOLDER_PREVIEW_NATIVE_SHADOW,
  FOLDER_PREVIEW_WEB_SHADOW,
  type FolderPreview,
  FolderTile,
} from './FolderTile';

/**
 * Internal render engine shared by FolderCard and StudyGroupCard. Unlike
 * DeckCard, folder cards have NO outer white container — the colour folder
 * shape is the card, with a folder-shaped body panel over its bottom. Grid
 * has a `compact` (180×180) and an `expanded` subvariant (folder 270×224 —
 * the revised Figma 5656:17083; study group still the pre-revision
 * 252×192). Up to 3 deck previews stack inside the folder (grid) / trail
 * the row (list).
 *
 * Slots differentiate the two cards:
 *   - `listGlyph`        — leading icon for the list row (folder / group icon).
 *   - `gridTileOverlay`  — laid over the grid tile (SG avatar stack).
 *   - `gridGlyphRow`     — the grid body's OWN first row (SG group icon),
 *                          sharing the line with the ⋯ actions; the title
 *                          moves to its own row below.
 *   - `listChipsLeading` — before the list chips row (SG avatar stack).
 */
export interface FolderLikeCardProps {
  view?: 'grid' | 'list';
  size?: 'compact' | 'expanded';
  title: string;
  color: string;
  /** Up to 3 deck previews (cover image, falling back to a white emoji page). */
  previews?: FolderPreview[];
  badges?: CardBadge[];
  /** Localized fallback badge shown when every chip's count is 0. */
  emptyLabel?: string;
  /**
   * Flashcards to do — the GRID card's top-right chip / the LIST row's
   * leading chip, and the flashcards chip's promotion to primary in both.
   * `0` hides it.
   */
  todoCount?: number;
  /** Localized to-do text ("To-do: 18"); falls back to the bare count. */
  todoLabel?: string;
  /** Hover copy spelling the To-do count out. */
  todoTooltip?: string;
  /**
   * A dragged deck/folder is over this card and would MOVE here: the card
   * grows (`folderDropCueClass`). The previews stay put — their lift / fan is
   * hover and press only, never a drop.
   */
  lifted?: boolean;
  /**
   * The item currently dragged over this folder (web dnd). Empty folders
   * render it as a half-opacity ghost page — see `FolderTile`.
   */
  dropPreview?: FolderPreview;
  /** Small indicator rendered right AFTER the title (shares the line). */
  titleTrailing?: ReactNode;
  onPress?: (event: PressEvent) => void;
  onActionsPress?: (event: PressEvent) => void;
  actionsLabel?: string;
  actionsSlot?: ReactNode;
  disabled?: boolean;
  className?: string;
  accessibilityLabel?: string;
  testID?: string;
  webProps?: PressableProps['webProps'];
  nativeProps?: PressableProps['nativeProps'];
  // Slots
  listGlyph: ReactNode;
  gridTileOverlay?: ReactNode;
  gridGlyphRow?: ReactNode;
  listChipsLeading?: ReactNode;
  /**
   * Taller grid body panel — the Figma "Study groups" card's one structural
   * difference from the folder card: its panel gains room for the group
   * glyph row. Set by StudyGroupCard; FolderCard keeps the default.
   */
  tallGridPanel?: boolean;
}

// List-stack hover spread (web): on row hover the thumbs slide subtly LEFT,
// a bit more per level under the top thumb, easing the tight overlap open.
// Indexed by depth-from-the-TOP thumb (the rightmost, rendered last).
// `FORCED` is the same shift applied statically while the row is PRESSED
// (touch's hover). Idle renders an explicit translate-x-0 — NativeWind needs
// the --tw-translate variables set from the first render (see FolderTile).
const THUMB_SPREAD_HOVER = [
  '',
  'web:group-hover/card:-translate-x-1',
  'web:group-hover/card:-translate-x-2',
];
const THUMB_SPREAD_FORCED = ['translate-x-0', '-translate-x-1', '-translate-x-2'];
// Unprefixed so the native press-spread eases like the web hover.
const THUMB_SPREAD_TRANSITION = 'transition-transform duration-200 ease-out';

/**
 * How far the folder ARTWORK sits inside the card's layout box on every side
 * — `FolderTile`'s `left-1 right-1 top-1` on the folder back. Overlays whose
 * spec is measured from the visible folder (the to-do chip) add it to their
 * card-box offsets.
 */
const FOLDER_ART_INSET = 4;

/**
 * Where the folder's TOP-RIGHT corner sits below the card's top edge. The tab
 * is on the LEFT, so the right-hand side starts at the shape's shoulder, not
 * at the artwork's top — and the two grid sizes put that shoulder at
 * different depths, which is why this is a per-size table and not one number:
 *
 *   compact  `FolderShape` viewBox 168×181, shoulder y=14.3564, drawn into a
 *            176-tall back → 4 + 14.3564 × 176/181 ≈ 18
 *   expanded viewBox 258×195, shoulder y=19.5508, drawn into a 191-tall back
 *            → 4 + 19.5508 × 191/195 ≈ 23
 *
 * The to-do chip hangs its 12px off THIS corner, so its top and right gaps
 * read equal against the nearest folder edge on both sizes.
 */
const FOLDER_SHOULDER_TOP = { compact: 18, expanded: 23 } as const;
/** The chip's gap from the folder's top-right corner, both axes. */
const TODO_CORNER_GAP = 12;

/**
 * List trailing: up to 3 previews as a tightly overlapping fan, tucked
 * toward the row's trailing edge (`-mr-2` — Figma 3153:44258/44255 place it
 * closer to the `⋮` than the row gap alone would).
 */
function ListPreviewStack({
  previews,
  pressed,
}: {
  previews: FolderPreview[];
  pressed: boolean;
}) {
  // Paint back → front like the grid stack: the FIRST preview is the TOP
  // (fully visible, rightmost) thumb, the rest peek out to its left.
  const items = previews.slice(0, 3).reverse();
  return (
    // px-locked frame + tuck: the 4px frame around the 78px fan is part of
    // the list row's fixed 102px height maths (see the container class).
    <View className="-mr-[8px] flex-row items-center p-[4px]">
      {items.map((p, i) => (
        <ListPreviewThumb
          key={i}
          preview={p}
          offset={i > 0}
          spreadDepth={items.length - 1 - i}
          pressed={pressed}
        />
      ))}
    </View>
  );
}

function ListPreviewThumb({
  preview,
  offset,
  spreadDepth,
  pressed,
}: {
  preview: FolderPreview;
  offset: boolean;
  /** Levels under the TOP (rightmost) thumb — picks the hover spread shift. */
  spreadDepth: number;
  /** Row is held down — force the spread (touch's hover). */
  pressed: boolean;
}) {
  const [failed, setFailed] = useState(false);
  const { isDark } = useTheme();
  const showImage = !!preview.image && !failed;
  // `relative` on EVERY thumb: the hover spread transforms only the thumbs
  // under the top one, and a transform alone promotes an element to the
  // positioned paint layer — mixing transformed and static siblings would
  // flip the stack's z-order mid-animation. All-positioned keeps paint
  // order = DOM order throughout.
  const spreadClasses = cn(
    'relative',
    THUMB_SPREAD_TRANSITION,
    THUMB_SPREAD_HOVER[spreadDepth],
    pressed ? THUMB_SPREAD_FORCED[spreadDepth] : 'translate-x-0',
  );
  if (preview.folderColor) {
    // Subfolder thumb — the DS preview folder (`FolderPreviewShape`, list
    // export 5686:89162): −300 fill + a −500 accent stroke, same treatment
    // as the grid stacks so a folder looks the same in both views. Its
    // shadow is the same `card-cover` weight the sibling A4 deck thumbs
    // carry, as a web drop-shadow FILTER / iOS alpha-derived layer shadow
    // so it hugs the tab notch — see FOLDER_PREVIEW_*_SHADOW in FolderTile
    // (no `elevation`: Android degrades shadowless, the DS convention).
    return (
      // 55×78 — the A4-format list preview (Figma 5726:91491).
      <View
        className={cn('h-[78px] w-[55px]', offset && '-ml-[40px]', spreadClasses)}
        webProps={{ style: { filter: FOLDER_PREVIEW_WEB_SHADOW } }}
        nativeProps={{ style: FOLDER_PREVIEW_NATIVE_SHADOW }}>
        <FolderPreviewShape color={preview.folderColor} size="list" />
      </View>
    );
  }
  // No-image thumb tinted with the deck's identity colour — same `tintHex`
  // pair as the grid page / DeckCard emoji tile.
  const tint =
    !showImage && preview.color
      ? {
          backgroundColor: tintHex(preview.color, 0.3, isDark),
          borderColor: tintHex(preview.color, 0.6, isDark),
        }
      : undefined;
  return (
    // 55×78 rounded-12 — the A4-format list preview (Figma 5714:29717).
    <View
      className={cn(
        'h-[78px] w-[55px] overflow-hidden rounded-xl border border-secondary-200 bg-secondary-50 web:shadow-card-cover',
        DARK_LIFTED,
        // No-media thumb: the deck emoji centred on the tinted sheet, the
        // same fallback the grid stack uses (`px-1`: a tag needs a gutter).
        !showImage && 'items-center justify-center px-1',
        offset && '-ml-[40px]',
        spreadClasses,
      )}
      webProps={tint && { style: tint }}
      nativeProps={tint && { style: tint }}>
      {showImage ? (
        <CardImage src={preview.image!} onError={() => setFailed(true)} />
      ) : (
        // Same label as the deck's own card (see CardTagLabel) — a tag drops
        // to 11px over two lines, the most an 8-character tag can read at on
        // a 55px-wide thumb.
        <CardTagLabel
          label={preview.emoji || DEFAULT_PREVIEW_EMOJI}
          color={preview.color}
          glyphSize={24}
          tagSize={11}
          tagLines={2}
        />
      )}
    </View>
  );
}

function FolderLikeCardImpl({
  view,
  size = 'compact',
  title,
  color,
  previews,
  badges,
  emptyLabel,
  todoCount,
  todoLabel,
  todoTooltip,
  lifted = false,
  dropPreview,
  titleTrailing,
  onPress,
  onActionsPress,
  actionsLabel,
  actionsSlot,
  disabled = false,
  className,
  accessibilityLabel,
  testID,
  webProps,
  nativeProps,
  listGlyph,
  gridTileOverlay,
  gridGlyphRow,
  listChipsLeading,
  tallGridPanel = false,
}: FolderLikeCardProps) {
  const resolvedView = view ?? 'grid';
  const items = useMemo(() => (previews ?? []).slice(0, 3), [previews]);
  // 1 on web/phones; tablets scale the whole card (see gridSize note).
  const scale = useResponsiveScale();
  const { isDark } = useTheme();
  // Press = touch's hover: while the card is held down the previews lift,
  // exactly like the web hover (and they stay lifted for the whole hold).
  //
  // It reaches exactly two things — the folder art's `lifted` and the list
  // fan's spread — and the memoized nodes below are what keep it there: the
  // title, the chip row and the ⋯ come back element-identical, so React bails
  // out of those subtrees instead of rebuilding them twice per tap.
  const [held, setHeld] = useState(false);
  const handlePressIn = useCallback(() => setHeld(true), []);
  const handlePressOut = useCallback(() => setHeld(false), []);
  // An Apple Pencil (or trackpad) held over the card does the same — the
  // native Pressable reports hover for pen/mouse pointers only, never a
  // finger. On web it lands on the same lift the CSS hover already draws.
  const [hovered, setHovered] = useState(false);
  const handleHoverIn = useCallback(() => setHovered(true), []);
  const handleHoverOut = useCallback(() => setHovered(false), []);
  const pressed = held || hovered;

  const mergedNativeProps = useMemo(
    () => ({
      ...nativeProps,
      style: [
        CARD_NATIVE_SHADOW[resolvedView],
        (nativeProps as { style?: unknown } | undefined)?.style,
        // The drop cue's grow — an inline style on native, never its class
        // (see `folderDropCueClass`).
        folderDropCueNativeStyle(lifted, resolvedView),
      ],
    }),
    [nativeProps, resolvedView, lifted],
  );

  const actions = useMemo(
    () =>
      actionsSlot ??
      (onActionsPress ? (
        <Button
          variant="ghost"
          // Figma sizes: the FOLDER grid card's "Icon Button" is 32×32 (= xs,
          // 3374:8689); the STUDY-GROUP grid card's and the list rows' is
          // 28×28 (= xxs, 3374:9953).
          size={resolvedView === 'grid' && !tallGridPanel ? 'xs' : 'xxs'}
          icon={resolvedView === 'grid' ? Ellipsis : EllipsisVertical}
          disabled={disabled}
          accessibilityLabel={actionsLabel}
          testID={subTestID(testID, 'menu')}
          onPress={(event: PressEvent) => {
            event?.stopPropagation?.();
            onActionsPress(event);
          }}
        />
      ) : null),
    [
      actionsSlot,
      onActionsPress,
      resolvedView,
      tallGridPanel,
      disabled,
      actionsLabel,
      testID,
    ],
  );

  // Fade clamp, not an ellipsis (designer, 2026-08) — see DeckCard's note:
  // the ramp reads as "there's more" without spending a character on it.
  // Compact panel row geometry, shared with DeckCard (see `compactPanelRows`).
  const compactRows = compactPanelRows('folder');
  const isCompact = resolvedView === 'grid' && size === 'compact';

  const titleNode = useMemo(
    () => (
      <MarqueeLabel
        overflow="fade"
        className={cn(
          cardTitleVariants({ view: resolvedView }),
          // Fixed-px tile → design type on native too (see COMPACT_TITLE_PIN).
          isCompact && COMPACT_TITLE_PIN,
        )}
        label={title}
        testID={subTestID(testID, 'title')}
      />
    ),
    [isCompact, resolvedView, title, testID],
  );

  // ONE chip row, built once per variant. The four call sites below (list +
  // three grid panels) render exactly one of them, and the props differ only by
  // view: list takes the size default and the tooltip, grid takes `s`/`xs` and
  // leaves the tooltip to the corner chip.
  const chipsNode = useMemo(
    () =>
      resolvedView === 'list' ? (
        <CardChips
          badges={badges}
          emptyLabel={emptyLabel}
          view="list"
          todoCount={todoCount}
          todoLabel={todoLabel}
          todoTooltip={todoTooltip}
        />
      ) : (
        <CardChips
          badges={badges}
          emptyLabel={emptyLabel}
          view="grid"
          badgeSize={size === 'expanded' ? 's' : 'xs'}
          todoCount={todoCount}
          todoLabel={todoLabel}
          pinned={isCompact}
        />
      ),
    [
      badges,
      emptyLabel,
      isCompact,
      resolvedView,
      size,
      todoCount,
      todoLabel,
      todoTooltip,
    ],
  );

  // Derived ABOVE the two view branches because one of them returns early and
  // a hook cannot sit under a `return`. Both are `style`/prop objects, which
  // React Native diffs BY REFERENCE — rebuilt per render they re-sent the glass
  // panel and the card box to native on every press.
  // What the panel's glass sees behind it (web fake-frost — see
  // CardBodyPanel's `folderCover`): the folder colour + the page stack.
  // Page fills mirror PreviewPage's: subfolder / tinted pages take their
  // 30% `tintHex`; image / plain pages read as the white sheet (undefined →
  // CardBodyPanel's theme sheet colour). Native ignores this entirely.
  const folderCover = useMemo(() => {
    const layoutKey = size === 'expanded' ? 'expanded' : 'compact';
    const pageLayout = FOLDER_PAGE_LAYOUTS[layoutKey][items.length] ?? [];
    return {
      folderColor: color,
      pages: pageLayout.map((slot, i) => {
        const p = items[pageLayout.length - 1 - i];
        return {
          insetX: slot.insetX,
          // Subfolder previews render at the FolderPreviewShape's −300 tone
          // (40% tint); deck emoji pages keep their 30% sheet.
          fill: p?.folderColor
            ? tintHex(p.folderColor, 0.4, isDark)
            : p?.color && !p.image
              ? tintHex(p.color, 0.3, isDark)
              : undefined,
        };
      }),
    };
  }, [color, items, size, isDark]);
  const gridBoxStyle = useMemo(
    () => (size === 'expanded' ? { width: 270 * scale, height: 224 * scale } : undefined),
    [size, scale],
  );
  const expandedPanelProps = useMemo(
    () =>
      size === 'expanded' ? { style: { height: Math.round(110 * scale) } } : undefined,
    [size, scale],
  );
  // The folder-colour slab under the panel — ONE object for both platforms
  // (web and native were building the same literal twice).
  const panelSlabStyle = useMemo(
    () => ({
      height: size === 'expanded' ? Math.round((110 - 32) * scale) : 98 - 32,
      backgroundColor: color,
      borderBottomLeftRadius: Math.round(21 * scale),
      borderBottomRightRadius: Math.round(21 * scale),
    }),
    [color, scale, size],
  );

  // The corner chip, memoized like the title and the chips row: it is another
  // `Badge` (so another `MarqueeLabel`) and it sits in the card body, which
  // re-renders on every press for the art's lift.
  const todoNode = useMemo(
    () => (
      /* To-do chip, 12px off the folder's TOP-RIGHT CORNER on both axes — the
         corner is the nearest border, so equal gaps are what reads as even.
         Rendered after the art region (so no folder page can cover it) and
         outside its clip. Folder cards lift their PAGES on hover, not the whole
         tile, so the chip stays put. */
      <CardTodoBadge
        tooltip={todoTooltip}
        count={todoCount}
        label={todoLabel}
        // Compact takes the raw offsets — its box is fixed px, so a scaled
        // corner inset would walk the chip inward as the ramp grew (see the
        // same split in DeckCard).
        top={
          isCompact
            ? FOLDER_SHOULDER_TOP[size] + TODO_CORNER_GAP
            : Math.round((FOLDER_SHOULDER_TOP[size] + TODO_CORNER_GAP) * scale)
        }
        right={
          isCompact
            ? FOLDER_ART_INSET + TODO_CORNER_GAP
            : Math.round((FOLDER_ART_INSET + TODO_CORNER_GAP) * scale)
        }
        pinned={isCompact}
      />
    ),
    [todoCount, todoLabel, todoTooltip, isCompact, size, scale],
  );

  const dropCue = folderDropCueClass(lifted, resolvedView);

  const commonProps = {
    as: 'div',
    onPress,
    onPressIn: handlePressIn,
    onPressOut: handlePressOut,
    onHoverIn: handleHoverIn,
    onHoverOut: handleHoverOut,
    // Light tick on touch-down alongside the press lift / thumb spread
    // (native only).
    haptic: true,
    disabled,
    accessibilityRole: 'button',
    accessibilityLabel: accessibilityLabel ?? title,
    testID,
    webProps,
    nativeProps: mergedNativeProps,
  } as const;

  if (resolvedView === 'list') {
    return (
      <Pressable
        {...commonProps}
        // FIXED height (the Figma 5714:29707 geometry: py-2 + p-1 + the
        // 78-tall A4 fan = 102) — not a min-h: every row renders the same
        // height whether or not it carries the trailing preview fan, so
        // multi-column tablet lists line up. pr-3: the ⋯ sits 12px off the
        // right border with or without previews (5714:29197), and the fan
        // lands 8px left of the ⋯ (its 4px frame − the −mr-2 tuck + gap-3).
        className={cn(
          // Flexible width: rows fill their column and cap at 560 — wider
          // than the Figma 360 design width on purpose, so tablet multi-column
          // layouts (cells ~430-520) are filled edge to edge instead of
          // leaving dead slack around a 360 row. 360 stays the FIT minimum
          // the list screens use for their column math. Paddings and gap are
          // ARBITRARY-PX, not rem utilities: the tablet rem ramp (×1.25)
          // would scale them while the 102px height and 78px fan stay fixed,
          // which pushed the fan past the card's edge on iPad.
          'group/card relative flex h-[102px] w-full max-w-[560px] flex-row items-center gap-[12px] rounded-[28px] border border-secondary-200 bg-secondary-50/70 py-[8px] pl-[20px] pr-[12px] web:shadow-card-row',
          // Dark: the content-surface level, like DeckCard's row (the 70%
          // mirrored `secondary-50` read as the bare page).
          DARK_LAYER1,
          dropCue,
          className,
        )}>
        {/* px-locked vertical gap — part of the row's fixed height maths. */}
        <View className="min-w-0 flex-1 gap-[8px]">
          <View className="w-full flex-row items-center gap-2">
            {listGlyph}
            {titleNode}
            {titleTrailing}
          </View>
          <View className="w-full flex-row items-center gap-2">
            {/* The leading slot (SG avatar stack) keeps its natural size;
                the chips row takes the REMAINING width (its own `w-full`
                resolves against this flex-1 box) — without the split, both
                claim the full row and paint over each other. */}
            {listChipsLeading ? (
              <View className="shrink-0">{listChipsLeading}</View>
            ) : null}
            <View className="min-w-0 flex-1">{chipsNode}</View>
          </View>
        </View>
        {items.length ? <ListPreviewStack previews={items} pressed={pressed} /> : null}
        {actions}
      </Pressable>
    );
  }

  // Grid — NO white container: the colour folder shape is the card, a
  // folder-shaped body panel covers its bottom.
  // Expanded tiles ASK for their design width (252px, × the responsive
  // scale) and cap at the container (`max-w-full`) so they shrink on narrow
  // phone columns. NOT `w-full max-w-[252px]`: several call sites wrap the
  // card in shrink-to-fit containers (inline-block links, dnd wrappers,
  // flex-wrap items) where a percentage width collapses to min-content.
  // Compact FILLS its cell (`w-full`, mirroring `cardVariants`): the host
  // grid solves the column count so the fill width lands inside the tile
  // engine's band, and the height holds at 180. Call sites in shrink-to-fit
  // wrappers state the 180 design box via `className` (merged after this,
  // so it wins).
  //
  // ALL the expanded card's fixed geometry multiplies by the app's
  // responsive scale (1 on web and phones; up to 1.25 on tablets, where
  // ResponsiveScaleProvider renders every rem-based token 25% larger): a
  // px-fixed box around rem-scaled type produced a cramped card whose
  // proportions no longer matched the web render. Scaling the box + panel +
  // row offsets by the same factor keeps the card PROPORTIONALLY identical
  // to web at every scale. Inline styles, not classes — immune to a stale
  // Tailwind cache and to the rem swap double-applying.
  const gridSize = size === 'expanded' ? 'max-w-full' : 'h-[180px] w-full';
  // The revised expanded card — folder 5656:17083 AND study group
  // 5729:10426, both 270×224: the folder back fills the top 195, the 110
  // panel hangs off the card bottom 29px BELOW the folder back.

  // Folder panels are the fixed Figma heights — and the height must MATCH the
  // panel path's viewBox aspect (see CardBodyPanel), or the shape stretches.
  // Expanded: folder 110 (5656:17097, same height as the deck panel), study
  // group 116 (3374:9948). Compact: 98 (the revised 5714:29353) for folder
  // AND study group — both share the one compact panel anatomy.
  const panelH = size === 'expanded' ? undefined : 'h-[98px]';
  const px = (n: number) => Math.round(n * scale);
  // The EXPANDED panels position via INLINE STYLES, not utility classes: the
  // panel is a fixed 116 (Figma 3374:9948), and the study-group variant
  // places its rows absolutely at the exact Figma offsets (3374:9946: glyph
  // row y=20 h=28, title y=52 h=24, chips y=80). Inline styles on purpose —
  // they are immune to a stale Tailwind/NativeWind cache that hasn't
  // generated a newly-introduced class yet, and absolute offsets can't
  // drift between web CSS and native Yoga the way flow layout did.
  const isExpandedTall = tallGridPanel && size === 'expanded';
  const isExpandedRegular = !tallGridPanel && size === 'expanded';
  // Both expanded panels are the 110 folder-expanded revision (5656:17095 /
  // 5729:10436); they differ only in content (the SG one leads the title
  // with its glyph).

  return (
    <Pressable
      {...commonProps}
      className={cn(
        'group/card relative flex flex-col rounded-[24px]',
        gridSize,
        dropCue,
        className,
      )}
      webProps={{ ...commonProps.webProps, style: gridBoxStyle }}
      nativeProps={{
        ...commonProps.nativeProps,
        style: [commonProps.nativeProps.style, gridBoxStyle],
      }}>
      {/* Folder-colour slab under the LOWER panel (DeckCard parity — its
          cover extends beneath the body panel). Without it the glass
          samples the page BACKGROUND wherever the folder art doesn't reach:
          the expanded panel's 29px hang below the 195 folder back, and the
          compact clip's bottom-corner crescents — blanching the panel's
          bottom. The folder back + pages still render ABOVE this, so their
          silhouettes through the glass are unchanged. Geometry keeps every
          slab pixel INSIDE the glass: the top starts 32 below the panel's
          top (both panel paths' side edges reach x=0/right by y≈21/32, so
          from 32 down the full-width slab is always covered), and the
          bottom corners round at the panel's 21. Web's fake-frost replica
          draws the same colour (CardBodyPanel's overscanned folder rect,
          clipped by the panel path itself). */}
      <View
        className="absolute inset-x-0 bottom-0"
        webProps={{ style: panelSlabStyle }}
        nativeProps={{ style: panelSlabStyle }}
      />
      {/* Folder art gets its OWN clip so the card never clips the panel: the
          panel's mask is its own rounded bottom edge, so its border can't be
          shaved thin nor its blur spill past it. Compact/SG bottom rounds
          generously (> the panel's widest stretched corner) so no preview /
          folder edge can peek under the panel's bottom corners. The revised
          folder expanded (5656:17083) instead FIXES the art region to the
          Figma 195 (the panel hangs 29px below it, so nothing can reach the
          panel's bottom corners and a bottom rounding would visibly shave
          the folder back's corners through the glass). */}
      <View
        // Full-card art region on BOTH sizes, so the preview pages (pinned
        // bottom-0) extend under the panel to the card's bottom — their
        // blurred silhouettes reach the glass's bottom like compact's. The
        // expanded FOLDER BACK keeps its fixed Figma 195 inside FolderTile
        // instead of the region fixing it. Compact adds the generous bottom
        // rounding; expanded's pages sit ≥34px off the sides so they can't
        // reach the panel's bottom corners.
        className={cn(
          'relative w-full flex-1 overflow-hidden rounded-t-[24px]',
          size === 'expanded' ? undefined : 'rounded-b-[40px]',
        )}>
        <FolderTile
          color={color}
          previews={items}
          lifted={pressed}
          size={size}
          dropPreview={dropPreview}
          overlay={gridTileOverlay}
        />
      </View>
      {/* To-do chip, 12px off the folder's TOP-RIGHT CORNER on both axes —
          the corner is the nearest border, so equal gaps are what reads as
          even. Rendered after the art region (so no folder page can cover
          it) and outside its clip. Folder cards lift their PAGES on hover,
          not the whole tile, so the chip stays put. */}
      {todoNode}
      <View
        // Pure position box — every variant's rows position absolutely
        // inside it (the chips bottom-anchor at the DS 16px rule).
        className={cn('absolute inset-x-0 bottom-0', panelH)}
        webProps={expandedPanelProps}
        nativeProps={expandedPanelProps}>
        <CardBodyPanel shape="folder" size={size} folderCover={folderCover} />
        {isExpandedTall ? (
          // Study-group EXPANDED — the folder-expanded revision with the
          // glyph INLINE on the title row (Figma 5729:10436, 270×110):
          // title row y=24 h=32 (16px glyph, 8px gap, 24-high title
          // centred), ending 8px before the ⋯; the ⋯ in its OWN slot at
          // y=24 / right 12, rendered LAST so it paints (and receives
          // touches) on top; chips y=66 h=28 at the DS 16px bottom rule.
          <>
            <View
              className="absolute flex-row items-center gap-2"
              webProps={{
                style: { top: px(24), height: px(32), left: px(16), right: px(52) },
              }}
              nativeProps={{
                style: { top: px(24), height: px(32), left: px(16), right: px(52) },
              }}>
              <View className="shrink-0">{gridGlyphRow}</View>
              {titleNode}
            </View>
            <View
              className="absolute left-4"
              webProps={{ style: { bottom: px(16), right: px(12) } }}
              nativeProps={{ style: { bottom: px(16), right: px(12) } }}>
              {chipsNode}
            </View>
            <View
              className="absolute flex-row items-center"
              webProps={{ style: { top: px(24), height: px(32), right: px(12) } }}
              nativeProps={{ style: { top: px(24), height: px(32), right: px(12) } }}>
              {actions}
            </View>
          </>
        ) : isExpandedRegular ? (
          // Folder expanded panel (Figma 5656:17098): title row (title +
          // 32px ⋯, 8px gap) at y=24 h=32 — the 24-high title centres at
          // y=28 — ending 13px off the RIGHT edge (the spec's pr is 13, not
          // 16), chips 16px off the left / bottom edges (right 13, where
          // the one-line row clips). Same absolute-positioning rationale as
          // the SG variant above.
          <>
            <View
              className="absolute left-4 flex-row items-center gap-2"
              webProps={{ style: { top: px(24), height: px(32), right: px(13) } }}
              nativeProps={{ style: { top: px(24), height: px(32), right: px(13) } }}>
              {titleNode}
              {actions}
            </View>
            <View
              className="absolute left-4"
              webProps={{ style: { bottom: px(16), right: px(13) } }}
              nativeProps={{ style: { bottom: px(16), right: px(13) } }}>
              {chipsNode}
            </View>
          </>
        ) : (
          // Compact panel — folder 5714:29353 / study group 5729:10283,
          // both 180×98, re-cut against the New-IA frame `171:17564`: title
          // ROW y=18 h=32 (the ⋯ button's box, with the 24px title line
          // centred in it — the old `top-[22px] h-6` stated the line instead,
          // which the tablet rem ramp grew to 30 and pushed into the chips),
          // from left 16 and ending 8px before the ⋯ — the study group's
          // glyph sits INLINE on that row before the title (16px glyph, 8px
          // gap, per 5729:10364); the ⋯ in its OWN slot at the same y, 12px
          // off the right (rendered last so it paints and receives touches on
          // top); chips bottom-anchored at the DS 16px rule.
          //
          // The offsets come from `compactPanelRows` in px — see the note on
          // `cardBodyVariants` for why a fixed-px panel may not hold rem
          // geometry.
          <>
            <View
              className="absolute flex-row items-center"
              webProps={{ style: compactRows.title }}
              nativeProps={{ style: compactRows.title }}>
              {gridGlyphRow ? <View className="shrink-0">{gridGlyphRow}</View> : null}
              {titleNode}
            </View>
            <View
              className="absolute"
              webProps={{ style: compactRows.chips }}
              nativeProps={{ style: compactRows.chips }}>
              {chipsNode}
            </View>
            <View
              className="absolute flex-row items-center"
              webProps={{ style: compactRows.actions }}
              nativeProps={{ style: compactRows.actions }}>
              {actions}
            </View>
          </>
        )}
      </View>
    </Pressable>
  );
}

/**
 * MEMOIZED for the same reason `DeckCard` is: these cards sit in lists whose
 * hosts re-render for reasons no single card can display, and unmemoized each
 * of those rebuilt every visible card's title, chip row, folder art and glass
 * panel. Both wrappers (`FolderCard`, `StudyGroupCard`) hand it stable slot
 * elements so the memo can actually hold.
 */
export const FolderLikeCard = memo(FolderLikeCardImpl);
(FolderLikeCard as { displayName?: string }).displayName = 'FolderLikeCard';
